import { ReactNode } from 'react';
import { useRouter } from 'next/router';
import LoadingSpinner from 'components/loading/LoadingSpinner';
import useBlwRouteGuard from 'auth/useBlwRouteGuard';
import { useProtectedUrlGuard } from 'auth/useProtectedUrlGuard';
import { useBoatDetails } from 'components/pdp/v3/hooks';
import { getAsString } from 'helpers';
import { useUserDetails } from 'auth/mutations/userHooks';
import boatsetterPaths from 'utils/boatsetterPaths';
import BlwEntryLayout from './BLWEntryLayout';
import BlwLayout from './BlwLayout';
import { useBreakpoint } from '../BreakpointProvider';

type ConnectBlwLayoutProps = {
  name?: string;
  children: ReactNode;
  className?: string;
  vh100?: boolean;
  stretch?: boolean;
  isProtected?: boolean;
  isHome?: boolean;
  noBackground?: boolean;
  isLoading?: boolean;
};

const ConnectBlwLayout = (props: ConnectBlwLayoutProps) => {
  const {
    name,
    children,
    vh100,
    stretch,
    isProtected = true,
    isHome: isHomeProp,
    className,
    noBackground = false,
    isLoading: isLoadingProp = false,
  } = props;

  const router = useRouter();
  const boatId = getAsString(router.query.id);

  const { boatDetails, isLoading: isBoatLoading } = useBoatDetails(boatId);
  const { isAuthenticated } = useUserDetails();

  const breakpoints = useBreakpoint();
  const smallScreen = breakpoints.xs || breakpoints.sm;

  useBlwRouteGuard(boatDetails);
  useProtectedUrlGuard(isProtected);

  const pagesWithContainer = ['/insurance', '/details', '/commercial', '/international', '/add-more-boats'].map(
    (s) => `${boatsetterPaths.listBoat}/${boatId || boatId}${s}`
  );
  const pagesWithNoFlex = ['/rental-options', '/review', '/location-pin', '/safety-standards'].map(
    (s) => `${boatsetterPaths.listBoat}/${boatId || boatId}${s}`
  );
  const pagesWithNoPadding = [
    '/insurance-type',
    'insurance-info',
    '/cancellation-policy',
    '/rules',
    '/advance-notice',
    '/multi-booking',
    '/features',
    '/duration',
    '/photos',
  ].map((s) => `${boatsetterPaths.listBoat}/${boatId || boatId}${s}`);
  const pagesWithNoPaddingMobile = [].map((s) => `${boatsetterPaths.listBoat}/${boatId || boatId}${s}`);
  const pagesWithBackgroundTop = ['/details', '/insurance', '/commercial', '/finished'].map(
    (s) => `${boatsetterPaths.listBoat}/${boatId || boatId}${s}`
  );

  const isHome = isHomeProp || router.asPath === boatsetterPaths.listBoat || router.asPath === boatsetterPaths.listBoat;
  const imageLoggedIn = smallScreen ? '/images/logged-in-hero-mobile.jpg' : '/images/logged-in-hero.jpg';
  const imageLoggedOut = smallScreen ? '/images/logged-out-hero-mobile-new.jpg' : '/images/logged-out-hero-new.jpg';
  const homeBackground = isAuthenticated ? imageLoggedIn : imageLoggedOut;
  const linearBackground = 'linear-gradient(162.71deg, #063735 -2.87%, #0E7873 59.26%, #80AC66 109.13%)';

  const getBackground = () => {
    if (noBackground) return null;
    return isHome ? homeBackground : linearBackground;
  };

  const background = getBackground();
  const wrapperSize = router.asPath.includes('add-more-boats') ? 'md' : 'xxl';

  const withContainer = isHome || pagesWithContainer.includes(router.asPath);
  const withNoFlex = pagesWithNoFlex.includes(router.asPath) || isHome;
  const noPadding = pagesWithNoPadding.includes(router.asPath);
  const noPaddingMobile = pagesWithNoPaddingMobile.includes(router.asPath);
  const backgroundTop = pagesWithBackgroundTop.includes(router.asPath);

  const isLoading = isBoatLoading || isLoadingProp;

  const loader = isLoading ? (
    <div style={{ minHeight: '100vh' }}>
      <div
        style={{
          position: 'fixed',
          top: '0px',
          left: '0px',
          width: '100vw',
          height: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <LoadingSpinner size="medium" color="#72D4BA" />
      </div>
    </div>
  ) : (
    children
  );

  if (isHome) {
    return (
      <BlwEntryLayout
        name={name}
        background={background}
        size={wrapperSize}
        withContainer={withContainer}
        withNoFlex={withNoFlex}
        isAuthenticated={isAuthenticated}
        noPadding={noPadding}
        isMobile={smallScreen}
        noPaddingMobile={noPaddingMobile}
        backgroundTop={backgroundTop}
        className={className}
      >
        {loader}
      </BlwEntryLayout>
    );
  }
  return (
    <BlwLayout
      name={name}
      className={className}
      isHome={isHome}
      background={background}
      size={wrapperSize}
      withContainer={withContainer}
      withNoFlex={withNoFlex}
      isAuthenticated={isAuthenticated}
      noPadding={noPadding}
      isMobile={smallScreen}
      noPaddingMobile={noPaddingMobile}
      backgroundTop={backgroundTop}
      vh100={vh100}
      stretch={stretch}
    >
      {loader}
    </BlwLayout>
  );
};

export default ConnectBlwLayout;
